import { QWERTY, QWERTY_STROKE_OF } from './keyboardGeometry';

/**
 * Avro Phonetic — Bangla typed the way it sounds in English letters: `ami` = আমি,
 * `bangla` = বাংলা, `kotha` = কথা. Like Bijoy, the tool does the conversion itself
 * from the physical keys, so the learner needs no Avro installed (and should turn it
 * off — the IME warning says so).
 *
 * The rules follow Avro's phonetic scheme:
 *  - a vowel after a consonant becomes its sign (k a = কা); at the start of a word or
 *    after another vowel it is the full letter (a i = আই, b o i = বই).
 *  - `o` after a consonant is the inherent vowel — it writes nothing, it only keeps
 *    the consonants apart (k o t = কত, while k t = ক্ত).
 *  - two consonants with nothing between them join into a conjunct (n d = ন্দ); so
 *    r before a consonant is the ref (r m = র্ম) and after one the র-ফলা (p r = প্র),
 *    y after a consonant is the য-ফলা (b y = ব্য) and w the ব-ফলা (s w = স্ব).
 *  - capitals pick the "hard" letters: T ট, D ড, N ণ, R ড়, S শ, Sh ষ, O ো, I ী, U ূ.
 *    Other capitals type the same as their small letter.
 *  - ng = ং, Ng = ঙ, NG = ঞ, gg = জ্ঞ, t`` = ৎ, : = ঃ, ^ = ঁ, . = ।, ` = a break
 *    (:` and .` type a plain colon and full stop).
 *
 * The reverse direction (`romanize`) writes any Bangla word as the keys that type it,
 * which is what the lessons, the next-key glow and the word check run on. A word the
 * scheme cannot type back exactly is left out of the practice word lists.
 */

const HAS = '্';
const NUKTA = '়';
const BN_DIGITS = '০১২৩৪৫৬৭৮৯';

/** latin → [independent letter, vowel sign] */
const VOWELS = {
    rri: ['ঋ', 'ৃ'], OI: ['ঐ', 'ৈ'], OU: ['ঔ', 'ৌ'], ee: ['ঈ', 'ী'], oo: ['উ', 'ু'],
    a: ['আ', 'া'], i: ['ই', 'ি'], I: ['ঈ', 'ী'], u: ['উ', 'ু'], U: ['ঊ', 'ূ'],
    e: ['এ', 'ে'], O: ['ও', 'ো'], o: ['অ', ''],
};

/** latin → consonant (nukta letters in their NFC, decomposed form) */
const CONSONANTS = {
    kh: 'খ', gh: 'ঘ', Ng: 'ঙ', ch: 'ছ', jh: 'ঝ', NG: 'ঞ', Th: 'ঠ', Dh: 'ঢ', th: 'থ', dh: 'ধ',
    ph: 'ফ', bh: 'ভ', sh: 'শ', Sh: 'ষ', Rh: `ঢ${NUKTA}`, gg: `জ${HAS}ঞ`,
    k: 'ক', g: 'গ', c: 'চ', j: 'জ', T: 'ট', D: 'ড', N: 'ণ', t: 'ত', d: 'দ', n: 'ন',
    p: 'প', f: 'ফ', b: 'ব', v: 'ভ', m: 'ম', z: 'য', Z: 'য', r: 'র', l: 'ল', S: 'শ',
    s: 'স', h: 'হ', R: `ড${NUKTA}`, Y: `য${NUKTA}`, q: 'ক',
};

/** Signs that sit on what came before and never join or take a vowel sign. */
const MARKS = { 't``': 'ৎ', ng: 'ং', ':': 'ঃ', '^': 'ঁ' };
/** Punctuation Avro would otherwise turn into Bangla: a backtick after it keeps it as is. */
const LITERALS = { ':`': ':', '.`': '.' };

const TOKENS = [...Object.keys(VOWELS), ...Object.keys(CONSONANTS), ...Object.keys(MARKS), ...Object.keys(LITERALS), 'y', 'w']
    .sort((a, b) => b.length - a.length);
// Capitals with a meaning of their own; any other capital types its small letter.
const CASED = new Set(['O', 'I', 'U', 'T', 'D', 'N', 'R', 'S', 'Y', 'Z']);

function tokenAt(text, i) {
    for (const token of TOKENS) {
        if (text.startsWith(token, i)) return token;
    }
    const ch = text[i];
    if (/[A-Z]/.test(ch) && !CASED.has(ch)) {
        const lower = ch.toLowerCase();
        if (TOKENS.includes(lower)) return { fold: lower };
    }
    return null;
}

/** Avro phonetic keys (a latin string) → Bangla Unicode (NFC). */
export function avroToBangla(latin) {
    let out = '';
    // 'cons' = a consonant still carrying its inherent vowel (the next consonant joins
    // it, the next vowel becomes its sign); 'vowel' = after a vowel; 'other' = neither.
    let prev = 'start';
    let i = 0;
    while (i < latin.length) {
        const found = tokenAt(latin, i);
        if (!found) {
            const ch = latin[i];
            if (ch === '`') prev = 'other';
            else if (ch === '.') { out += '।'; prev = 'other'; }
            else if (ch === '$') { out += '৳'; prev = 'other'; }
            else if (/[0-9]/.test(ch)) { out += BN_DIGITS[Number(ch)]; prev = 'other'; }
            else { out += ch; prev = /\s/.test(ch) ? 'start' : 'other'; }
            i += 1;
            continue;
        }
        const token = typeof found === 'string' ? found : found.fold;
        i += typeof found === 'string' ? found.length : 1;
        if (VOWELS[token]) {
            const [full, sign] = VOWELS[token];
            out += prev === 'cons' ? sign : full;
            prev = 'vowel';
        } else if (CONSONANTS[token]) {
            out += (prev === 'cons' ? HAS : '') + CONSONANTS[token];
            prev = 'cons';
        } else if (LITERALS[token]) {
            out += LITERALS[token];
            prev = 'other';
        } else if (MARKS[token]) {
            out += MARKS[token];
            prev = 'other';
        } else if (token === 'y') {
            // য-ফলা after a consonant, য় anywhere else.
            out += prev === 'cons' ? `${HAS}য` : `য${NUKTA}`;
            prev = 'cons';
        } else if (token === 'w') {
            // ব-ফলা after a consonant, ও anywhere else.
            if (prev === 'cons') { out += `${HAS}ব`; } else { out += 'ও'; prev = 'vowel'; }
        }
    }
    return out.normalize('NFC');
}

/* ------------------------------------------------------------- Bangla → keys */

/** consonant → its keys: first is the canonical spelling, the rest also type it */
const CONS_KEYS = {
    'ক': ['k'], 'খ': ['kh'], 'গ': ['g'], 'ঘ': ['gh'], 'ঙ': ['Ng'], 'চ': ['c'], 'ছ': ['ch'],
    'জ': ['j'], 'ঝ': ['jh'], 'ঞ': ['NG'], 'ট': ['T'], 'ঠ': ['Th'], 'ড': ['D'], 'ঢ': ['Dh'],
    'ণ': ['N'], 'ত': ['t'], 'থ': ['th'], 'দ': ['d'], 'ধ': ['dh'], 'ন': ['n'], 'প': ['p'],
    'ফ': ['f', 'ph'], 'ব': ['b'], 'ভ': ['bh', 'v'], 'ম': ['m'], 'য': ['z'], 'র': ['r'],
    'ল': ['l'], 'শ': ['sh', 'S'], 'ষ': ['Sh'], 'স': ['s'], 'হ': ['h'],
    [`ড${NUKTA}`]: ['R'], [`ঢ${NUKTA}`]: ['Rh'], [`য${NUKTA}`]: ['y', 'Y'],
};
/** a consonant after ্ inside a cluster, where Avro has its own key */
const JOINED_KEYS = { 'য': ['y', 'z'], 'ব': ['w', 'b'] };
const SIGN_KEYS = {
    'া': ['a'], 'ি': ['i'], 'ী': ['I', 'ee'], 'ু': ['u'], 'ূ': ['U'], 'ৃ': ['rri'],
    'ে': ['e'], 'ৈ': ['OI'], 'ো': ['O'], 'ৌ': ['OU'],
};
const INDEP_KEYS = {
    'অ': ['o'], 'আ': ['a'], 'ই': ['i'], 'ঈ': ['I', 'ee'], 'উ': ['u'], 'ঊ': ['U'], 'ঋ': ['rri'],
    'এ': ['e'], 'ঐ': ['OI'], 'ও': ['O'], 'ঔ': ['OU'],
};
const MARK_KEYS = { 'ৎ': 't``', 'ং': 'ng', 'ঃ': ':', 'ঁ': '^' };
const PUNCT_KEYS = { '।': '.', '৳': '$', ':': ':`', '.': '.`' };

const isConsonant = (ch) => !!CONS_KEYS[ch];

/** Split a Bangla word into pieces: a consonant cluster (+ its vowel sign), a full vowel, a mark, or another character. */
function pieces(word) {
    const chars = Array.from(word);
    const out = [];
    let i = 0;
    const consAt = (j) => {
        if (chars[j + 1] === NUKTA && CONS_KEYS[chars[j] + NUKTA]) return chars[j] + NUKTA;
        return isConsonant(chars[j]) ? chars[j] : null;
    };
    while (i < chars.length) {
        const first = consAt(i);
        if (first) {
            const cluster = [first];
            let j = i + first.length;
            while (chars[j] === HAS && consAt(j + 1)) {
                const next = consAt(j + 1);
                cluster.push(next);
                j += 1 + next.length;
            }
            if (chars[j] === HAS) return null; // a visible hasanta — Avro needs extra keys for it
            const sign = SIGN_KEYS[chars[j]] ? chars[j] : null;
            if (sign) j += 1;
            out.push({ kind: 'cons', cluster, sign, text: chars.slice(i, j).join('') });
            i = j;
            continue;
        }
        const ch = chars[i];
        if (INDEP_KEYS[ch]) out.push({ kind: 'vowel', text: ch, keys: INDEP_KEYS[ch] });
        else if (MARK_KEYS[ch]) out.push({ kind: 'mark', text: ch, keys: [MARK_KEYS[ch]] });
        else if (PUNCT_KEYS[ch]) out.push({ kind: 'other', text: ch, keys: [PUNCT_KEYS[ch]] });
        else if (BN_DIGITS.includes(ch)) out.push({ kind: 'other', text: ch, keys: [String(BN_DIGITS.indexOf(ch))] });
        else if (/[ঀ-৿]/.test(ch)) return null; // a Bangla sign Avro cannot type this way
        else out.push({ kind: 'other', text: ch, keys: [ch] });
        i += 1;
    }
    return out;
}

/** Every key spelling of a consonant cluster, canonical first (capped). */
function clusterSpellings(cluster) {
    let spellings = [''];
    for (let k = 0; k < cluster.length; k += 1) {
        let keys;
        if (cluster[k] === 'জ' && cluster[k + 1] === 'ঞ') {
            keys = ['gg', 'jNG']; // জ্ঞ
            k += 1;
        } else {
            keys = (k > 0 && JOINED_KEYS[cluster[k]]) || CONS_KEYS[cluster[k]];
        }
        spellings = spellings.flatMap((s) => keys.map((key) => s + key));
    }
    return Array.from(new Set(spellings)).slice(0, 6);
}

/**
 * A word as typing units: each piece of the word with every key spelling that types
 * it, the canonical one first. A consonant left bare before another consonant or a
 * full vowel carries an `o` (the inherent vowel), or the two would join. Null when the
 * word cannot be typed back exactly.
 */
function avroUnits(word) {
    const parts = pieces(word);
    if (!parts || !parts.length) return null;
    const units = parts.map((part, idx) => {
        if (part.kind !== 'cons') return { text: part.text, spellings: part.keys };
        let spellings = clusterSpellings(part.cluster);
        if (part.sign) {
            spellings = spellings.flatMap((s) => SIGN_KEYS[part.sign].map((v) => s + v));
        } else {
            const next = parts[idx + 1];
            const needsO = next && (next.kind === 'cons' || next.kind === 'vowel' || next.kind === 'mark');
            if (needsO) spellings = spellings.map((s) => `${s}o`);
            // At the end of a word the inherent o is optional (bakyo = baky = বাক্য).
            else if (!next) spellings = [...spellings, ...spellings.map((s) => `${s}o`)];
        }
        return { text: part.text, spellings: Array.from(new Set(spellings)).slice(0, 6) };
    });
    // Neighbouring units can run together into a different token (s + h = sh); a
    // backtick keeps them apart. Add one only where the plain spelling reads wrong.
    const target = word.normalize('NFC');
    const canonical = () => units.map((u) => u.spellings[0]).join('');
    if (avroToBangla(canonical()) !== target) {
        for (let k = 0; k < units.length - 1; k += 1) {
            const left = units[k].spellings[0];
            const right = units[k + 1].spellings[0];
            if (/[A-Za-z`]$/.test(left) && /^[A-Za-z]/.test(right) && avroToBangla(left + right) !== avroToBangla(left) + avroToBangla(right)) {
                units[k].spellings = units[k].spellings.map((s) => `${s}\``);
            }
        }
        if (avroToBangla(canonical()) !== target) return null;
    }
    return units;
}

const unitCache = new Map();
function cachedUnits(word) {
    if (!unitCache.has(word)) unitCache.set(word, avroUnits(word));
    return unitCache.get(word);
}

/** The keys that type a Bangla word in Avro (canonical spelling), or null. */
export function romanize(word) {
    const units = cachedUnits(normalizeAvroText(word));
    return units ? units.map((u) => u.spellings[0]).join('') : null;
}

/** Whether the scheme can type this word back exactly. */
export const avroCanType = (word) => !!cachedUnits(normalizeAvroText(word));

/** Bangla passage text folded to what Avro types: no joiners, Bangla digits, ASCII quotes. */
export function normalizeAvroText(text) {
    return String(text || '')
        .normalize('NFC')
        .replace(/[‌‍]/g, '')
        .replace(/[0-9]/g, (d) => BN_DIGITS[Number(d)])
        .replace(/[‘’]/g, "'")
        .replace(/[“”]/g, '"')
        .replace(/[–—]/g, '-')
        .replace(/ /g, ' ');
}

/* ----------------------------------------------------------- layout object */

const strokeOf = (ch) => QWERTY_STROKE_OF[ch] || null;
const charOf = (stroke) => {
    const pair = QWERTY[stroke.code];
    return pair ? pair[stroke.shift ? 1 : 0] : '';
};

/** What a key types, shown small on the on-screen key. */
const SOUND_OF = {
    a: 'া', b: 'ব', c: 'চ', d: 'দ', e: 'ে', f: 'ফ', g: 'গ', h: 'হ', i: 'ি', j: 'জ', k: 'ক',
    l: 'ল', m: 'ম', n: 'ন', o: 'অ', p: 'প', q: 'ক', r: 'র', s: 'স', t: 'ত', u: 'ু', v: 'ভ',
    w: 'ও', x: '', y: 'য়', z: 'য', O: 'ো', I: 'ী', U: 'ূ', T: 'ট', D: 'ড', N: 'ণ', R: 'ড়',
    S: 'শ', Y: 'য়', Z: 'য', ':': 'ঃ', '^': 'ঁ', '.': '।', $: '৳',
};

export const avroLayout = {
    id: 'avro',
    script: 'bengali',
    phonetic: true,
    name: 'Bangla (Avro Phonetic)',
    nameBn: 'বাংলা (অভ্র ফোনেটিক)',
    legend: (code) => {
        const pair = QWERTY[code];
        if (!pair) return null;
        if (/^[a-z]$/.test(pair[0])) {
            // The key's letter, with the Bangla it types (and its capital's, if different) above.
            const small = SOUND_OF[pair[0]] || '';
            const big = SOUND_OF[pair[1]];
            const hint = big && big !== small ? `${small} ${big}` : small;
            return { plain: pair[1], shift: hint || null };
        }
        const hint = SOUND_OF[pair[0]] || SOUND_OF[pair[1]];
        return { plain: pair[0], shift: hint || pair[1] };
    },
    normalizeText: normalizeAvroText,
    encodeWord(word) {
        const units = cachedUnits(normalizeAvroText(word));
        if (!units) {
            // Not typeable by the scheme: fall back to the letters themselves so the run never breaks.
            return Array.from(normalizeAvroText(word)).map((ch) => ({ text: ch, options: [] })).filter((u) => u.options.length);
        }
        return units.map((u) => ({
            text: u.text,
            options: u.spellings
                .map((keys) => Array.from(keys).map(strokeOf))
                .filter((strokes) => strokes.every(Boolean)),
        })).filter((u) => u.options.length);
    },
    render(strokes) {
        return avroToBangla(strokes.map(charOf).join(''));
    },
    /** The latin letters actually typed, for the preview under the input. */
    rawText(strokes) {
        return strokes.map(charOf).join('');
    },
    romanize,
    strokeLabel(stroke) {
        if (!stroke) return '';
        if (stroke.code === 'Space') return '␣';
        return charOf(stroke);
    },
    /** Per-key statistics count letters (a and A together), like English. */
    statKey(stroke) {
        if (!stroke || stroke.code === 'Space') return null;
        const pair = QWERTY[stroke.code];
        if (!pair) return null;
        return /^[a-z]$/.test(pair[0]) ? pair[0] : pair[stroke.shift ? 1 : 0];
    },
};
